<template>
<m-pageframe
    title="Texas Do Not Call List"
>
<v-container
    fluid
    class="pa-4 pa-sm-6"
    :class="$vuetify.breakpoint.mdAndUp ? 'max-width-1200' : ''"
>
    <!-- Page Header -->
    <div class="mb-4">
        <h1 class="text-h4 font-weight-medium mb-2">Texas Do Not Call List</h1>
        <p class="text-subtitle-1 grey--text text--darken-1">
            Upload and sync the Texas Do Not Call list from 
            <a href="https://www.texasnocall.com" target="_blank">texasnocall.com</a>.
            The system will add new entries, update timestamps for existing entries, and remove entries no longer in the file.
        </p>
    </div>

    <!-- Current Stats Card -->
    <v-card class="mb-4" outlined>
        <v-card-title class="grey lighten-4">
            <v-icon left color="primary">mdi-database</v-icon>
            Current Texas DNC List Data
        </v-card-title>
        <v-card-text class="pt-4">
            <div v-if="loading_stats" class="text-center py-4">
                <v-progress-circular indeterminate color="primary"></v-progress-circular>
            </div>
            <div v-else>
                <div class="d-flex align-center mb-3">
                    <div>
                        <div class="text-h4 font-weight-bold primary--text">
                            {{ formatNumber(current_count) }}
                        </div>
                        <div class="text-subtitle-1 grey--text">entries in the Texas DNC list</div>
                        <div v-if="last_updated_time" class="text-caption grey--text mt-1">
                            <v-icon x-small class="mr-1">mdi-clock-outline</v-icon>
                            Last updated: {{ formatDate(last_updated_time) }}
                        </div>
                    </div>
                    <v-spacer></v-spacer>
                    <v-btn text color="primary" @click="loadStats" :loading="loading_stats">
                        <v-icon left>mdi-refresh</v-icon>
                        Refresh
                    </v-btn>
                </div>
                <v-divider class="my-3"></v-divider>
                <div class="d-flex align-center">
                    <div>
                        <div class="text-subtitle-2 grey--text text--darken-1">Bulk Message Filter Label</div>
                        <div class="text-body-1 mt-1">
                            "Remove recipients on the <strong>{{ date_label }}</strong> Texas Do Not Call (DNC) list"
                        </div>
                    </div>
                    <v-spacer></v-spacer>
                    <v-btn
                        small
                        outlined
                        color="primary"
                        @click="onClickEditDateLabel"
                    >
                        <v-icon small left>mdi-pencil</v-icon>
                        Edit Date
                    </v-btn>
                </div>
            </div>
        </v-card-text>
    </v-card>

    <!-- Upload Section -->
    <v-card class="mb-4" outlined>
        <v-card-title class="grey lighten-4">
            <v-icon left color="primary">mdi-upload</v-icon>
            Upload StateList.txt
        </v-card-title>
        <v-card-text class="pt-4">
            <v-alert type="info" dense outlined class="mb-4">
                <div>
                    <div class="mb-1">
                        Download the latest <strong>StateList.txt</strong> file from 
                        <a href="https://www.texasnocall.com" target="_blank">texasnocall.com</a>
                        and upload it here to sync the database.
                    </div>
                    <div class="text-caption">
                        <v-icon x-small class="mr-1">mdi-information-outline</v-icon>
                        All entries in the file will have their timestamp updated. Entries not in the file will be removed.
                        This is how we track which numbers were seen in the latest file.
                    </div>
                </div>
            </v-alert>

            <v-expansion-panels flat class="mb-4">
                <v-expansion-panel>
                    <v-expansion-panel-header class="grey lighten-4">
                        <div class="d-flex align-center">
                            <v-icon small class="mr-2">mdi-file-document-outline</v-icon>
                            <span class="subtitle-2">View File Format Example</span>
                        </div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                        <div class="pt-3">
                            <p class="body-2 mb-2">
                                The StateList.txt file is tab-separated with three columns:
                            </p>
                            <v-simple-table dense class="file-example mb-3">
                                <thead>
                                    <tr class="grey lighten-3">
                                        <th>Area Code</th>
                                        <th>Phone Number</th>
                                        <th>Registration Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td><code>210</code></td>
                                        <td><code>2105551234</code></td>
                                        <td><code>04/01/2012</code></td>
                                    </tr>
                                    <tr>
                                        <td><code>512</code></td>
                                        <td><code>5125559876</code></td>
                                        <td><code>01/15/2018</code></td>
                                    </tr>
                                    <tr>
                                        <td><code>979</code></td>
                                        <td><code>9795554321</code></td>
                                        <td><code>07/22/2020</code></td>
                                    </tr>
                                </tbody>
                            </v-simple-table>
                            <v-btn small outlined color="primary" @click="downloadExample">
                                <v-icon left small>mdi-download</v-icon>
                                Download Example File
                            </v-btn>
                        </div>
                    </v-expansion-panel-content>
                </v-expansion-panel>
            </v-expansion-panels>

            <v-file-input
                v-model="selected_file"
                label="Select StateList.txt file"
                accept=".txt"
                prepend-icon="mdi-file-document"
                :disabled="is_processing"
                show-size
                outlined
                dense
                class="mb-2"
            ></v-file-input>

            <!-- Processing Progress -->
            <div v-if="is_processing" class="mt-4">
                <div class="d-flex align-center mb-2">
                    <v-icon small color="primary" class="mr-2">mdi-cog-sync</v-icon>
                    <span class="text-subtitle-2">{{ progress_status }}</span>
                </div>
                <v-progress-linear
                    :value="progress_percentage"
                    height="20"
                    striped
                    color="primary"
                    rounded
                >
                    <template v-slot:default>
                        <strong class="white--text">{{ progress_percentage }}%</strong>
                    </template>
                </v-progress-linear>
            </div>

            <!-- Preview Results -->
            <div v-if="preview_result" class="mt-4">
                <v-card outlined>
                    <v-card-title class="grey lighten-4">
                        <v-icon left color="primary">mdi-eye</v-icon>
                        Preview Results
                    </v-card-title>
                    <v-card-text class="pt-4">
                        <p class="text-body-1 mb-4">
                            Review the changes below before importing. This will sync the database to match the uploaded file.
                        </p>
                        <v-row>
                            <v-col cols="12" md="4">
                                <v-card outlined class="fill-height stat-card">
                                    <v-card-text class="text-center pa-4">
                                        <v-icon large color="primary" class="mb-2">mdi-file-document-outline</v-icon>
                                        <div class="text-h4 primary--text font-weight-bold">
                                            {{ formatNumber(preview_result.total_in_file) }}
                                        </div>
                                        <div class="text-subtitle-2 grey--text text--darken-1 mt-1">Total in File</div>
                                    </v-card-text>
                                </v-card>
                            </v-col>
                            <v-col cols="12" md="4">
                                <v-card outlined class="fill-height stat-card">
                                    <v-card-text class="text-center pa-4">
                                        <v-icon large color="blue" class="mb-2">mdi-update</v-icon>
                                        <div class="text-h4 blue--text text--darken-1 font-weight-bold">
                                            {{ formatNumber(preview_result.unchanged) }}
                                        </div>
                                        <div class="text-subtitle-2 grey--text text--darken-1 mt-1">Already Exist</div>
                                        <div class="text-caption grey--text">(timestamp will be updated)</div>
                                    </v-card-text>
                                </v-card>
                            </v-col>
                            <v-col cols="12" md="4">
                                <v-card outlined class="fill-height stat-card">
                                    <v-card-text class="text-center pa-4">
                                        <v-icon large color="primary" class="mb-2">mdi-swap-horizontal</v-icon>
                                        <div class="d-flex justify-center align-center">
                                            <div class="mr-4 text-center">
                                                <div class="success--text text-h5 font-weight-bold">+{{ formatNumber(preview_result.to_add) }}</div>
                                                <div class="caption grey--text">to add</div>
                                            </div>
                                            <v-divider vertical class="mx-2"></v-divider>
                                            <div class="text-center">
                                                <div class="error--text text-h5 font-weight-bold">-{{ formatNumber(preview_result.to_remove) }}</div>
                                                <div class="caption grey--text">to remove</div>
                                            </div>
                                        </div>
                                        <div class="text-subtitle-2 grey--text text--darken-1 mt-2">Changes</div>
                                    </v-card-text>
                                </v-card>
                            </v-col>
                        </v-row>
                        
                        <!-- Large Deletion Warning -->
                        <v-alert 
                            v-if="preview_result.has_large_deletion_warning" 
                            type="warning" 
                            outlined 
                            class="mt-4"
                        >
                            <div class="d-flex align-start">
                                <v-icon color="warning" class="mr-2 mt-1">mdi-alert</v-icon>
                                <div>
                                    <div class="font-weight-bold">Large Deletion Warning</div>
                                    <div class="text-body-2">{{ preview_result.warning_message }}</div>
                                </div>
                            </div>
                        </v-alert>
                    </v-card-text>
                    <v-divider></v-divider>
                    <v-card-actions class="pa-4">
                        <v-btn 
                            :color="preview_result.has_large_deletion_warning ? 'warning' : 'primary'" 
                            @click="executeImport"
                            :loading="is_processing"
                        >
                            <v-icon left>mdi-check</v-icon>
                            {{ preview_result.has_large_deletion_warning ? 'Proceed with Import' : 'Confirm and Import' }}
                        </v-btn>
                        <v-btn 
                            text 
                            @click="cancelPreview" 
                            class="ml-2"
                        >
                            Cancel
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </div>

            <!-- Results Display -->
            <div v-if="import_result" class="mt-4">
                <!-- Success Result -->
                <v-card v-if="import_result.success" outlined>
                    <v-card-title class="lighten-4">
                        <v-icon left color="success">mdi-check-circle</v-icon>
                        Import Complete
                    </v-card-title>
                    <v-card-text class="pt-4">
                        <v-row>
                            <v-col cols="6" sm="3">
                                <div class="text-center">
                                    <v-icon color="primary" class="mb-1">mdi-file-document-outline</v-icon>
                                    <div class="text-h5 font-weight-bold primary--text">
                                        {{ formatNumber(import_result.total_in_file) }}
                                    </div>
                                    <div class="caption grey--text">Total in file</div>
                                </div>
                            </v-col>
                            <v-col cols="6" sm="3">
                                <div class="text-center">
                                    <v-icon color="success" class="mb-1">mdi-plus-circle-outline</v-icon>
                                    <div class="text-h5 font-weight-bold success--text">
                                        +{{ formatNumber(import_result.added) }}
                                    </div>
                                    <div class="caption grey--text">Added</div>
                                </div>
                            </v-col>
                            <v-col cols="6" sm="3">
                                <div class="text-center">
                                    <v-icon color="error" class="mb-1">mdi-minus-circle-outline</v-icon>
                                    <div class="text-h5 font-weight-bold error--text">
                                        -{{ formatNumber(import_result.removed) }}
                                    </div>
                                    <div class="caption grey--text">Removed</div>
                                </div>
                            </v-col>
                            <v-col cols="6" sm="3">
                                <div class="text-center">
                                    <v-icon color="blue" class="mb-1">mdi-update</v-icon>
                                    <div class="text-h5 font-weight-bold blue--text text--darken-1">
                                        {{ formatNumber(import_result.unchanged) }}
                                    </div>
                                    <div class="caption grey--text">Already Existed</div>
                                </div>
                            </v-col>
                        </v-row>
                        <div class="text-center mt-3 grey--text text-caption">
                            Completed in {{ (import_result.duration_ms / 1000).toFixed(1) }} seconds
                        </div>
                    </v-card-text>
                </v-card>
                <!-- Error Result -->
                <v-alert v-else type="error" outlined dense>
                    <div class="d-flex align-center">
                        <v-icon color="error" class="mr-2">mdi-alert-circle</v-icon>
                        <div>
                            <div class="font-weight-bold">Import Failed</div>
                            <div class="text-body-2">{{ import_result.error }}</div>
                        </div>
                    </div>
                </v-alert>
            </div>
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
            <v-btn
                color="primary"
                :loading="is_processing"
                :disabled="!selected_file || is_processing || !!preview_result"
                @click="previewFile"
            >
                <v-icon left>mdi-eye</v-icon>
                Preview Changes
            </v-btn>
        </v-card-actions>
    </v-card>
</v-container>
</m-pageframe>
</template>

<script>
export default {
    name: 'admin-texas-dnc-list',
    
    data() {
        return {
            current_count: 0,
            last_updated_time: null,
            date_label: '',
            date_label_knob_code: '',
            date_label_raw: '',
            loading_stats: false,
            selected_file: null,
            is_processing: false,
            progress_percentage: 0,
            progress_status: '',
            process_id: null,
            progress_interval: null,
            import_result: null,
            preview_result: null,
            uploaded_blob_id: null
        };
    },

    mounted() {
        this.loadStats();
    },

    beforeDestroy() {
        this.stopPolling();
    },

    methods: {
        formatNumber(num) {
            return num ? num.toLocaleString() : '0';
        },

        formatDate(timestamp) {
            if (!timestamp) return '';
            const date = new Date(timestamp);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        },

        downloadExample() {
            // Generate example file content
            const content = [
                '210\t2105551234\t04/01/2012',
                '512\t5125559876\t01/15/2018',
                '979\t9795554321\t07/22/2020',
                '817\t8175558765\t03/10/2015',
                '214\t2145553456\t11/05/2019'
            ].join('\n');
            
            // Create download link
            const blob = new Blob([content], { type: 'text/plain' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'StateList_example.txt';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        },

        loadStats() {
            this.loading_stats = true;
            this.$rc.makeApiCall({
                url: 'admin/texas-dnc-list/stats',
                method: 'get',
                on_response: 'onResponseStandard',
                on_response_params: {
                    on_success: (response) => {
                        this.current_count = response.current_count || 0;
                        this.last_updated_time = response.last_updated_time || null;
                        this.date_label = response.date_label_formatted || '';
                        this.date_label_raw = response.date_label_raw || '';
                        this.date_label_knob_code = response.date_label_knob_code || '';
                        this.loading_stats = false;
                    },
                    on_error: () => {
                        this.loading_stats = false;
                    }
                }
            });
        },

        onClickEditDateLabel()
        {
            this.$rc.prompt({
                'title': 'Edit Texas DNC Date Label',
                'label': 'Date (YYYY-MM-DD format)',
                'value': this.date_label_raw,
                'is_valid': (value) => {
                    // Validate YYYY-MM-DD format
                    const date_regex = /^\d{4}-\d{2}-\d{2}$/;
                    if (!date_regex.test(value))
                    {
                        return false;
                    }
                    // Also validate it's a valid date
                    const parts = value.split('-');
                    const month = parseInt(parts[1], 10);
                    const day = parseInt(parts[2], 10);
                    if (month < 1 || month > 12 || day < 1 || day > 31)
                    {
                        return false;
                    }
                    return true;
                },
                'after_prompt': (value) => {
                    this.$rc.rest({
                        'spinner': true,
                        'endpoint': 'PUT: /api/v1/admin/knob/:code',
                        'params': {
                            'code': this.date_label_knob_code,
                            'value': value,
                        },
                        'on_success': () => {
                            this.loadStats();
                        },
                        'on_error': 'Unable to update date label',
                    });
                }
            });
        },

        previewFile() {
            if (!this.selected_file) return;

            this.is_processing = true;
            this.progress_percentage = 0;
            this.progress_status = 'Uploading file...';
            this.import_result = null;
            this.preview_result = null;
            this.requires_confirmation = false;

            const form_data = new FormData();
            form_data.append('dnc_file', this.selected_file);
            form_data.append('preview_only', 'true');

            this.$rc.makeApiCall({
                url: 'admin/texas-dnc-list/upload',
                method: 'post',
                form_data: form_data,
                timeout: 10 * 60 * 1000,
                on_response: 'onResponseStandard',
                on_response_params: {
                    on_success: this.onUploadSuccess,
                    on_error: this.onUploadError
                }
            });
        },

        executeImport() {
            if (!this.selected_file && !this.uploaded_blob_id) return;

            this.is_processing = true;
            this.progress_percentage = 0;
            this.progress_status = 'Uploading file...';
            this.preview_result = null;

            const form_data = new FormData();
            if (this.uploaded_blob_id) {
                form_data.append('blob_id', this.uploaded_blob_id);
            } else {
                form_data.append('dnc_file', this.selected_file);
            }
            form_data.append('preview_only', 'false');

            this.$rc.makeApiCall({
                url: 'admin/texas-dnc-list/upload',
                method: 'post',
                form_data: form_data,
                timeout: 10 * 60 * 1000,
                on_response: 'onResponseStandard',
                on_response_params: {
                    on_success: this.onUploadSuccess,
                    on_error: this.onUploadError
                }
            });
        },



        onUploadSuccess(response) {
            this.process_id = response.process_id;
            this.uploaded_blob_id = response.blob_id;
            this.progress_status = 'Processing started...';
            this.startPolling();
        },

        onUploadError(error) {
            this.is_processing = false;
            this.progress_status = '';
            this.import_result = {
                success: false,
                error: error?.message || 'Upload failed'
            };
        },

        startPolling() {
            this.progress_interval = setInterval(() => {
                this.checkProgress();
            }, 1000);
        },

        stopPolling() {
            if (this.progress_interval) {
                clearInterval(this.progress_interval);
                this.progress_interval = null;
            }
        },

        checkProgress() {
            if (!this.process_id) return;

            this.$rc.makeApiCall({
                url: `process/${this.process_id}`,
                method: 'get',
                on_response: 'onResponseStandard',
                on_response_params: {
                    on_success: this.onProgressUpdate,
                    on_error: this.onProgressError
                }
            });
        },

        onProgressUpdate(pte) {
            const status = pte.status || {};
            
            if (status.progress !== undefined) {
                this.progress_percentage = status.progress;
            }
            if (status.phase) {
                this.progress_status = status.phase;
            }

            if (pte.state === 'finished') {
                this.onProcessComplete(pte);
            } else if (pte.state === 'failed') {
                this.onProcessFailed(pte);
            }
        },

        onProcessComplete(pte) {
            this.stopPolling();
            this.is_processing = false;
            this.progress_percentage = 100;

            const output = pte.output || {};
            
            if (output.preview) {
                // Preview results with optional warning
                this.preview_result = {
                    total_in_file: output.total_in_file || 0,
                    current_count: output.current_count || 0,
                    to_add: output.to_add || 0,
                    to_remove: output.to_remove || 0,
                    unchanged: output.unchanged || 0,
                    has_large_deletion_warning: output.has_large_deletion_warning || false,
                    warning_message: output.warning_message || null
                };
            } else {
                this.import_result = {
                    success: output.success !== false,
                    total_in_file: output.total_in_file || 0,
                    added: output.added || 0,
                    removed: output.removed || 0,
                    unchanged: output.unchanged || 0,
                    duration_ms: output.duration_ms || 0
                };
                this.selected_file = null;
                this.uploaded_blob_id = null;
                this.loadStats();
            }
        },

        onProcessFailed(pte) {
            this.stopPolling();
            this.is_processing = false;
            this.import_result = {
                success: false,
                error: pte.failure_reason || 'Unknown error'
            };
        },

        onProgressError() {
            // Progress check error - no action needed
        },

        cancelPreview() {
            this.preview_result = null;
            this.uploaded_blob_id = null;
        },


    }
};
</script>

<style scoped>
.max-width-1200 {
    max-width: 1200px;
}
.transparent {
    background: transparent !important;
}
.stat-card {
    transition: box-shadow 0.2s ease;
}
.stat-card:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}
</style>
